import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { AlertTriangle } from 'lucide-react'
import {
  FILING_CONFLICTS_KEY,
  fetchFilingConflicts,
  setClassFiling,
  type FilingConflict,
} from '../api/classAdmin'

/**
 * Classes whose makeup filing has not been settled (D-506, widened by D-507).
 *
 * <b>Three problems, one list.</b> A name claiming technique while the filing
 * denies it; a class nobody has filed at all; and a technique class missing the
 * style or the level the matcher reads. They arrive differently and they are
 * fixed the same way, which is why they share a screen — and why each row says
 * which of the three it is.
 *
 * <b>Recital is absent because a makeup there repairs nothing.</b> All 28 are
 * Recreational, and rec is measured but never gated (PRD 7.6) — so the credit a
 * repair earns moves a number nobody acts on. A style would decide as little
 * as one on a routine. See <c>MakeupRepairPath.NeedsFiling</c>.
 *
 * <b>The detection has existed since D-415 and nobody could act on it.</b> The
 * import warns per class, the class panel flags per class, and D-415 wrote the
 * gap down in its own words: <i>"the warning is only seen while an import is
 * running."</i> Its advice was to fix it in Jackrabbit — a different system, a
 * re-import, and the owner's scarcest resource — so what was missing was a list
 * she could work through where she already is.
 *
 * <b>Why it earns a screen rather than a note.</b> A class named for technique
 * but filed as choreography sends a family to a paid private instead of a free
 * scheduled makeup (D-405). That is the cost D-415 named, and it lands hardest
 * straight after a fresh import, when nobody has looked at anything yet.
 *
 * <b>Still reported, never corrected</b> (D-51, D-227). Every row offers and
 * none of them guesses: either the dropdown is wrong or the name is, and only
 * the studio knows which. The category the name implies is offered as the
 * obvious answer, not applied as one.
 *
 * <b>All three fields, because two of them refuse silently.</b> An empty style
 * or level is an outright refusal in the matcher, so filing the category and
 * stopping there leaves the class exactly as unusable as before — the trap
 * somebody fixing these by eye falls into, and the reason the row says what is
 * still missing rather than leaving it to be noticed.
 */
export default function FilingConflicts({ defaultOpen = false }: { defaultOpen?: boolean }) {
  const { data, isPending } = useQuery({
    queryKey: FILING_CONFLICTS_KEY,
    queryFn: fetchFilingConflicts,
  })

  /*
   * <b>Shut until she asks, because the work is not the point of that screen</b>
   * (D-565). `asked`, on a screenshot of the real studio: <i>"the classes
   * needing makeup filing settled maybe should be able to be collapsed?"</i>
   *
   * Thirteen rows, each a three-field form with its own paragraph of
   * instructions, sat above *All classes* - roughly 2,300px of somebody
   * else's task in front of the list she opened the screen for. The count
   * still shouts in warn colours; what moved behind the press is the doing.
   *
   * <b>The instructions went inside with the rows.</b> They are how to fill the
   * three fields in, so they are only ever read by somebody who has opened
   * it - and left outside they were most of the height the fold was meant to
   * remove.
   *
   * <b>Open on the import screen, and that is why this is a prop rather than
   * a default.</b> D-506 put this component on two doors deliberately, and said
   * of that one that it is <i>when somebody is most likely to look</i>. There is
   * no class list under it there, so the height argument does not apply -
   * folding it there would have been this change quietly widening past what
   * was asked for, into the one place the panel most needs to be read.
   *
   * <b>Shutting it discards whatever was typed into the open rows.</b> They are
   * unmounted rather than hidden, so the three fields go back to what the
   * server last said. That is the safe direction - it is the <i>stale</i> half of
   * this pair that has bitten before (D-506, D-280) - but it is worth knowing
   * that *Hide* is not only concealment.
   */
  const [open, setOpen] = useState(defaultOpen)

  if (isPending || !data || data.length === 0) return null

  return (
    <section className="overflow-hidden rounded-2xl border border-warn bg-surface">
      {/*
        <b>The heading wraps the button, not the other way round.</b> A button
        makes its whole subtree presentational, so an `h2` inside one is not a
        heading to anybody using headings to navigate — and it is invalid HTML
        besides, since a button holds phrasing content. This is the standard
        disclosure shape and it keeps both.
      */}
      <h2 className="text-[13.5px] font-bold">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          className="flex w-full items-start gap-2.5 bg-warn-bg px-4 py-3 text-left hover:bg-warn-bg/70"
        >
          <AlertTriangle size={16} strokeWidth={2.5} className="mt-0.5 shrink-0 text-warn" />
          {/*
            <b>"Needs settling", not "cannot be made up"</b> (D-507). The
            stronger sentence is false for two real rows: a `COMP_TAP+TECH`
            filed as choreography works perfectly if the *name* is the wrong
            half, and a routine is repaired by a private. The list holds
            classes whose filing has not been agreed, which is true of all of
            them.
          */}
          <span>
            {data.length === 1
              ? 'One class needs its makeup filing settled'
              : `${data.length} classes need their makeup filing settled`}
          </span>
          <span className="ml-auto shrink-0 font-semibold text-ink-2">
            {open ? 'Hide' : data.length === 1 ? 'Settle it' : 'Settle them'}
          </span>
        </button>
      </h2>

      {open && (
        <p className="border-t border-line px-4 py-3 text-[12px] text-ink-2">
          The filing decides what can repair a missed session. Until each of these is
          settled, nobody can book a makeup for the class and it cannot be offered as
          one &mdash; a class filed as choreography sends the family to a paid private
          instead. A choreography class needs nothing else, because a routine is
          repaired by a private. A technique class needs a style and the levels it
          covers &mdash; write <span className="tabular">2/5</span> for a class covering
          2 through 5.
        </p>
      )}

      {/*
        Removed rather than hidden with a class: `hidden` is CSS, jsdom applies
        none, and a test asserting the rows are shut would pass with the fold
        deleted (D-564, one screen over).
      */}
      {open && (
        <ul className="divide-y divide-line border-t border-line">
          {data.map((row) => (
            /*
              <b>Keyed on the values, not just the id</b> (D-506).
  
              The three fields are `useState` initialised from the row, and
              `useState` does not re-initialise when the prop changes — so after
              the class panel one click away saved a style, this row still held
              the old blank one, and pressing *File it* wrote that blank back over
              the save. That is precisely the D-280 shape this feature cites as
              its reason for being a narrow route, arriving through the client
              instead. Keying on the values remounts the row when the data really
              changed, and leaves it alone when it did not.
            */
            <ConflictRow
              key={`${row.classId}:${row.category}:${row.style}:${row.levelBand}`}
              row={row}
            />
          ))}
        </ul>
      )}
    </section>
  )
}

const control =
  'rounded-lg border border-line-2 bg-surface px-2 py-1.5 text-[12.5px] text-ink focus:border-accent focus:outline-none'

function ConflictRow({ row }: { row: FilingConflict }) {
  const queryClient = useQueryClient()

  const [category, setCategory] = useState(row.category ?? '')
  const [style, setStyle] = useState(row.style ?? '')
  const [levelBand, setLevelBand] = useState(row.levelBand ?? '')

  const save = useMutation({
    mutationFn: () =>
      setClassFiling(row.classId, {
        category: category === '' ? null : category,
        style: style.trim() === '' ? null : style.trim(),
        levelBand: levelBand.trim() === '' ? null : levelBand.trim(),
      }),
    onSuccess: () => {
      /*
       * The list and every open class, because a row leaving this list is a
       * class that just changed (D-496). Filing one here is exactly the write
       * that used to leave a panel showing the kind it had a moment ago.
       */
      queryClient.invalidateQueries({ queryKey: FILING_CONFLICTS_KEY })
      queryClient.invalidateQueries({ queryKey: ['admin-classes'] })
      queryClient.invalidateQueries({ queryKey: ['admin-class'] })
    },
  })

  // Filing it as technique is what the name already says, so it is offered as
  // the obvious answer rather than applied as one (D-51, D-227).
  const unchanged =
    (row.category ?? '') === category &&
    (row.style ?? '') === style &&
    (row.levelBand ?? '') === levelBand

  return (
    <li className="px-4 py-3">
      {/* Machine-shaped names do not wrap on their own (CalendarWeekView). */}
      <p className="text-[13px] font-semibold wrap-anywhere">{row.name}</p>

      {/*
        <b>Why this row is here, in its own words</b> (D-507). The list holds
        three different problems now — a name contradicting its filing, a class
        nobody filed at all, and a technique class missing what the matcher
        reads — and they are not the same job. Leaving somebody to work that
        out from three fields is how thirteen rows get the same treatment and
        several get the wrong one.
      */}
      <p className="mt-0.5 text-[11.5px] text-ink-3">
        {row.nameDisagrees
          ? `Named for technique but filed as ${row.category}`
          : row.category === null
            ? 'Not filed at all'
            : `Filed as ${row.category}`}
        {row.needsStyle && ', with no style'}
        {row.needsLevel && ', with no level'}
        {row.touchedByHand && ' · set by hand, so imports leave it alone'}
      </p>

      <div className="mt-2 flex flex-wrap items-end gap-2">
        <label className="flex min-w-[7.5rem] flex-col gap-1">
          <span className="text-[11.5px] font-semibold text-ink-3">Repaired by</span>
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            aria-label={`How a missed session of ${row.name} is made up`}
            className={control}
          >
            <option value="">Not set</option>
            <option value="Tech">Technique</option>
            <option value="Choreo">Choreography</option>
            <option value="Recital">Recital</option>
          </select>
        </label>

        <label className="flex min-w-[7rem] flex-1 flex-col gap-1">
          <span className="text-[11.5px] font-semibold text-ink-3">Style</span>
          <input
            value={style}
            onChange={(e) => setStyle(e.target.value)}
            placeholder="Tap, ballet, acro&hellip;"
            aria-label={`The style ${row.name} covers`}
            className={control}
          />
        </label>

        <label className="flex w-24 flex-col gap-1">
          <span className="text-[11.5px] font-semibold text-ink-3">Level</span>
          <input
            value={levelBand}
            onChange={(e) => setLevelBand(e.target.value)}
            placeholder="2/3"
            aria-label={`Levels ${row.name} covers`}
            className={`${control} tabular text-center`}
          />
        </label>

        {/*
          <b>Said here as well as on the panel, because the trap is worse
          here</b> (D-506). The class name sits directly above this box and
          often reads <c>| 2-5 |</c>, so copying it is the obvious move — and a
          hyphen is not a separator: <c>2-5</c> stores as one unreadable level
          whose number is 2, quietly withholding makeups from the 4s and 5s.
          It caught the person building this screen, which is the argument for
          the sentence rather than against it.
        */}
        <p className="w-full text-[11.5px] text-ink-3">
          Levels combine with a slash &mdash; write <span className="tabular">2/5</span> for a
          class covering 2 through 5, not <span className="tabular">2-5</span>. Leave it empty
          for a class with no level.
        </p>

        <button
          type="button"
          disabled={unchanged || save.isPending}
          onClick={() => save.mutate()}
          className="rounded-lg border border-transparent bg-accent px-3 py-1.5 text-[12.5px] font-semibold text-on-accent disabled:opacity-40"
        >
          {save.isPending ? 'Filing…' : 'File it'}
        </button>
      </div>

      {save.isError && (
        <p role="alert" className="mt-1.5 text-[12px] font-semibold text-crit">
          {(save.error as Error).message}
        </p>
      )}
    </li>
  )
}
